import type { ReactNode } from 'react';

import { usePositionedPopup } from '../../hooks/use-positioned-popup/use-positioned-popup';
import { type DropdownItems, Dropdown } from '../dropdown/dropdown-shared';
import { useDropdown } from '../dropdown/utilities';
import type { FieldContainerBaseProps } from '../field-container/field-container';
import { FieldContainer } from '../field-container/field-container';

export interface DropdownSearchFieldProps<Value = string>
  extends FieldContainerBaseProps {
  defaultValue?: Value;
  id: string;
  name: string;
  placeholder?: string;
  items: DropdownItems<Value>;
  'data-testid'?: string;
  'aria-describedby'?: string;
  size?: 'medium' | 'large';
  noResultsLabel: ReactNode;
  /**
   * Override of logic to show outline error styling on dropdown without validation messages
   */
  showVisualErrorStyles?: boolean;
}

export const DropdownSearchField = <Value,>({
  disabled = false,
  items,
  placeholder,
  label,
  helpText,
  defaultValue,
  'aria-invalid': invalid,
  'aria-describedby': errorMessageId,
  name,
  id,
  size = 'medium',
  showVisualErrorStyles = false,
  showField = true,
  errors,
  readOnly = false,
  noResultsLabel,
}: DropdownSearchFieldProps<Value>) => {
  'use no memo';
  const {
    menuItems,
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    getLabelProps,
  } = useDropdown({
    id,
    items,
    defaultValue,
  });

  const value = selectedItem?.value || '';

  const { setFloating, setReference, floatingStyles, constrainStyles } =
    usePositionedPopup({
      open: isOpen,
    });

  if (!showField) {
    return null;
  }

  const menuProps = getMenuProps();

  return (
    <FieldContainer
      {...getLabelProps()}
      label={label}
      aria-invalid={invalid}
      errorMessageId={errorMessageId}
      errors={errors}
      helpText={helpText}
      disabled={disabled}
      readOnly={readOnly}
    >
      <Dropdown.SearchInput
        size={size}
        disabled={disabled}
        readOnly={readOnly}
        name={name}
        isOpen={isOpen}
        toggleButtonProps={getToggleButtonProps()}
        getInputProps={getInputProps}
        ref={setReference}
        placeholder={placeholder}
        selectedItem={selectedItem}
        showVisualErrorStyles={showVisualErrorStyles}
        aria-invalid={invalid}
        aria-errormessage={errorMessageId} // https://stackoverflow.com/questions/51554395/what-is-the-difference-between-aria-errormessage-and-aria-alert
      />

      <Dropdown.Menu
        floatingStyles={floatingStyles}
        setFloating={setFloating}
        disabled={disabled}
        isOpen={isOpen}
        constrainStyles={constrainStyles}
        menuProps={menuProps}
      >
        {menuItems.length > 0 ? (
          menuItems.map((item, index) => (
            <Dropdown.MenuItem
              highlightedIndex={highlightedIndex}
              item={item}
              index={index}
              size={size}
              key={`${item.value || index}`}
              isSelected={value === item.value}
              {...getItemProps({ item, index })}
            />
          ))
        ) : (
          <Dropdown.NoResults>{noResultsLabel}</Dropdown.NoResults>
        )}
      </Dropdown.Menu>
    </FieldContainer>
  );
};
